import React, { useEffect, useState } from 'react'
import { EntityLayout, Row } from '../../types/layout.types'
import { Modal, Skeleton360Loader, SvgEdit, SvgExpandModal } from '@ui-kit'
import { FieldValues } from 'react-hook-form'
import AddEditRecordPage from '../../pages/AddEditViewRecord.page'
import {
  getEntityData,
  getEntityDataByIdEmbedded,
} from '../../apis/entityData.api'
import { useParams } from 'react-router-dom'
import { AgentDeskParams } from '../../types/params.types'
import {
  flattenEntityRelationshipTree,
  getLayoutTree,
  LayoutResponseType,
  LayoutRowType,
  RelationshipFormattedTreeTypes,
} from '@app-store'
import {
  FIELD_VALUE_CONSTANTS,
  LAYOUT_TYPE_NAMESPACE,
  MODE_CONSTANTS,
  ROW_TYPES,
  VIEW_360_CONSTANTS,
} from '../../utils/constants'
import Multilookup360Wrapper from './Multilookup360Wrapper'
import TabLayout from '../tabs/TabLayout'
import MultiLookupRenderTabs from '../tabs/MultiLookupRenderTabs'
import RowRenderTabs from '../tabs/RowRenderTabs'
import ThreadsNotesAccordion from '../ThreadsNotesAccordion'

const CardRender = ({
  card,
  parentLayout,
  cardListLayout,
  cardEditLayout,
}: {
  card: LayoutRowType
  parentLayout: EntityLayout
  cardListLayout: EntityLayout | undefined
  cardEditLayout: EntityLayout | undefined
  // middleCard: boolean
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isExpandModalOpen, setIsExpandModalOpen] = useState<boolean>(false)
  const [cardData, setCardData] = useState<FieldValues>({})
  // const [relationshipTree, setRelationshipTree] = useState<
  //   RelationshipFormattedTreeTypes[]
  // >([])
  const { id } = useParams<AgentDeskParams>()

  const onEditClick = () => {
    setIsEditModalOpen(true)
  }

  const handleSave = () => {
    setIsEditModalOpen(false)
    window.location.reload()
  }

  const fetchRelationshipTree = async ({
    entityId,
  }: {
    entityId: string
  }): Promise<{ treeObject: RelationshipFormattedTreeTypes[] }> => {
    try {
      const response = await getLayoutTree(
        entityId || '',
        FIELD_VALUE_CONSTANTS?.ONE?.toString()
      )

      if (response?.data) {
        const flattenedTree = flattenEntityRelationshipTree(response.data)
        // setRelationshipTree(flattenedTree);
        return { treeObject: flattenedTree }
      } else {
        return { treeObject: [] }
      }
    } catch (error) {
      console.error(error)
      return { treeObject: [] }
    }
  }

  const fetchLayoutData = async () => {
    const { treeObject } = await fetchRelationshipTree({
      entityId: parentLayout?.entity_id.toString(),
    })
    if (
      parentLayout?.layout_data?.added_fields?.[card?.keyName ?? '']
        ?.mapped_as === VIEW_360_CONSTANTS?.MANYTOONE ||
      parentLayout?.layout_data?.added_fields?.[card?.keyName ?? '']
        ?.mapped_as === VIEW_360_CONSTANTS?.ONETOONE
    ) {
      let lookupKeyname = ''
      treeObject.forEach((relation) => {
        if (relation.entity_name === cardListLayout?.entity_name) {
          lookupKeyname = relation.field_name?.toString() ?? ''
        }
      })

      let lookupDataId = ''
      try {
        try {
          const res = await getEntityDataByIdEmbedded(
            parentLayout?.entity_name,
            parentLayout?.entity_id,
            [lookupKeyname],
            Number(id)
          )
          lookupDataId = res?.data?.[lookupKeyname]?.id
        } catch (err) {
          console.error(err)
        }
        const response = await getEntityData(
          cardListLayout?.entity_name ?? '',
          Number(cardListLayout?.entity_id),
          Number(cardListLayout?.id),
          Number(lookupDataId)
        )

        if (response) {
          setCardData(response?.data)
        }
      } catch (err) {
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    } else {
      try {
        const response = await getEntityData(
          cardListLayout?.entity_name ?? '',
          Number(cardListLayout?.entity_id),
          Number(cardListLayout?.id),
          Number(id)
        )

        if (response) {
          setCardData(response?.data)
        }
      } catch (err) {
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    }
  }

  useEffect(() => {
    fetchLayoutData()
  }, [cardListLayout, parentLayout])

  if (isLoading) {
    return <Skeleton360Loader />
  }

  return (
    <div>
      {isExpandModalOpen && (
        <Modal
          isOpen={isExpandModalOpen}
          closeModal={() => {
            setIsExpandModalOpen(false)
          }}
          width="w-[80%]"
          isResizable
        >
          <AddEditRecordPage
            mode={MODE_CONSTANTS?.VIEW}
            entityId={cardListLayout?.entity_id?.toString()}
            id={cardData?.id}
            loaderVisible={false}
            allowNavigate={false}
            onSaveClick={handleSave}
            shouldModalOpen={false}
            onCancel={() => {
              setIsExpandModalOpen(false)
            }}
            showFooter={false}
            showButtonsInView={false}
            layoutId={cardListLayout?.id}
          />
        </Modal>
      )}
      {isEditModalOpen && (
        <Modal
          isOpen={isEditModalOpen}
          closeModal={() => {
            setIsEditModalOpen(false)
            fetchLayoutData()
          }}
          width="w-[80%]"
          isResizable
        >
          <div className="">
            {cardEditLayout ? (
              <AddEditRecordPage
                mode={MODE_CONSTANTS?.EDIT}
                entityId={cardEditLayout?.entity_id?.toString()}
                id={cardData?.id}
                loaderVisible={false}
                allowNavigate={false}
                onSaveClick={handleSave}
                shouldModalOpen={false}
                onCancel={() => {
                  setIsEditModalOpen(false)
                }}
                showFooter={true}
                layoutId={cardEditLayout?.id}
              />
            ) : null}
          </div>
        </Modal>
      )}
      <div className="bg-accent-0 rounded border w-full border-border-300 p-3 h-fit border-l-secondary-500 border-l-4">
        {(() => {
          const field =
            parentLayout?.layout_data?.added_fields?.[card?.keyName ?? '']
          const mappedAs = field?.mapped_as

          switch (mappedAs) {
            case VIEW_360_CONSTANTS.TAB:
              return (
                <TabLayout
                  layout={cardListLayout as LayoutResponseType}
                  recordId={id ?? ''}
                  entityId={cardListLayout?.entity_id.toString() ?? ''}
                  isWorkflow
                />
              )

            case VIEW_360_CONSTANTS.ROOT:
            case VIEW_360_CONSTANTS.MANYTOONE:
            case VIEW_360_CONSTANTS.ONETOONE:
              return (
                <>
                  {/* Heading */}
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-xs font-primary">
                      {field?.layout_name}
                    </span>
                    <div className="flex gap-2 items-center justify-center cursor-pointer">
                      {cardEditLayout && <SvgEdit onClick={onEditClick} />}
                      <SvgExpandModal
                        onClick={() => setIsExpandModalOpen(true)}
                      />
                    </div>
                  </div>
                  <hr className="w-full h-[0.08rem] bg-secondary-100 mt-4" />

                  {/* Rows Render */}
                  {cardListLayout?.layout_data?.pages?.[0]?.rows?.map(
                    (row, _index) => (
                      <div className="py-4" key={`${row?.label}-${_index}`}>
                        {(() => {
                          switch (row?.rowType) {
                            case ROW_TYPES?.MULTI_LOOKUP:
                              return (
                                <Multilookup360Wrapper
                                  heading={row?.label}
                                  entityId={
                                    cardListLayout?.layout_data?.added_fields?.[
                                      row?.lookup_meta?.lookup_keyName ?? ''
                                    ]?.d_entity_id?.toString() ?? ''
                                  }
                                  listViewId={
                                    cardListLayout?.layout_data?.added_fields?.[
                                      row?.lookup_meta?.lookup_keyName ?? ''
                                    ]?.list_view_layout_id ?? ''
                                  }
                                  editViewId={
                                    cardListLayout?.layout_data?.added_fields?.[
                                      row?.lookup_meta?.lookup_keyName ?? ''
                                    ]?.create_layout_id ?? ''
                                  }
                                  relationshipIdentifier={
                                    cardListLayout?.layout_data?.added_fields?.[
                                      row?.lookup_meta?.lookup_keyName ?? ''
                                    ]?.relationship?.relation_identifier ?? ''
                                  }
                                  lookupKeyname={
                                    row?.lookup_meta?.lookup_keyName ?? ''
                                  }
                                  parentEditLayoutId={
                                    cardEditLayout?.id?.toString() ?? ''
                                  }
                                  recordId={cardData?.id ?? id}
                                  entityIdParent={parentLayout?.entity_id.toString()}
                                  isAssign={
                                    parentLayout?.layout_data?.added_fields?.[
                                      card?.keyName ?? ''
                                    ]?.is_assign || true
                                  }
                                />
                              )
                            case ROW_TYPES.SINGLE_LOOKUP_LISTING:
                              return null
                            case ROW_TYPES.NOTES:
                            case ROW_TYPES.NOTE:
                              return (
                                <ThreadsNotesAccordion
                                  relationIdentifier={
                                    cardListLayout?.layout_data?.added_fields?.[
                                      row?.lookup_meta?.lookup_keyName ?? ''
                                    ]?.relationship?.relation_identifier ?? ''
                                  }
                                  row={row}
                                  layoutData={cardListLayout}
                                  id={id ?? ''}
                                  mode={MODE_CONSTANTS?.VIEW}
                                />
                              )

                            default:
                              return (
                                <RowRenderTabs
                                  row={row}
                                  layout={cardListLayout as LayoutResponseType}
                                  data={cardData}
                                  entityId={cardListLayout?.entity_id.toString()}
                                  index={_index}
                                />
                              )
                          }
                        })()}
                      </div>
                    )
                  )}
                </>
              )

            case VIEW_360_CONSTANTS.MANYTOMANY:
            case VIEW_360_CONSTANTS.ONETOMANY:
              if (
                field?.type === ROW_TYPES.NOTES ||
                field?.type === ROW_TYPES.NOTE ||
                field?.type === ROW_TYPES.THREAD
              )
                return (
                  <ThreadsNotesAccordion
                    relationIdentifier={card?.relation_identifier ?? ''}
                    row={card as Row}
                    id={id ?? '0'}
                    mode={MODE_CONSTANTS.VIEW}
                    createLayoutId={cardEditLayout?.id?.toString() ?? ''}
                    entityName={cardEditLayout?.entity_name?.toString() ?? ''}
                    entityId={cardEditLayout?.entity_id?.toString()}
                  />
                )
              else
                return (
                  <MultiLookupRenderTabs
                    heading={
                      field?.layout_name ??
                      LAYOUT_TYPE_NAMESPACE?.DEFAULT_OVERVIEW
                    }
                    listingLayout={cardListLayout as LayoutResponseType}
                    editLayout={cardEditLayout as LayoutResponseType}
                    relationIdentifier={card?.relation_identifier ?? ''}
                    layout={parentLayout as LayoutResponseType}
                    recordId={cardData?.id ?? id}
                    entityId={parentLayout?.entity_id.toString() || ''}
                    isAssign={
                      parentLayout?.layout_data?.added_fields?.[
                        card?.keyName ?? ''
                      ]?.is_assign || false
                    }
                  />
                )

            default:
              return null
          }
        })()}
      </div>
    </div>
  )
}

export default CardRender
